/**
 * <s-count-up> — counts a money value up or down when it changes.
 *
 * Markup: <s-count-up data-key="cart-total" data-value="{{ cart.total_price }}"><bdi>{{ cart.total_price | money }}</bdi></s-count-up>
 * - data-key: stable name; the last value seen for a key is kept for the page's lifetime,
 *   so a partial re-render (new element) animates from the previous total to the new one.
 * - data-value: the target amount in minor units (as Liquid prints prices).
 * The server-rendered text is the final state; animation only rewrites the digits in between.
 * Skipped on first render and when the shopper prefers reduced motion.
 */
const lastValues = new Map();
const DURATION = 600;

class SCountUp extends HTMLElement {
  #frame = 0;

  connectedCallback() {
    const key = this.dataset.key;
    const target = Number(this.dataset.value);
    if (!key || !Number.isFinite(target)) return;

    const previous = lastValues.get(key);
    lastValues.set(key, target);
    if (previous === undefined || previous === target) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#animate(previous / 100, target / 100);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
  }

  #textNode() {
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      if (/\d/.test(walker.currentNode.nodeValue)) return walker.currentNode;
    }
    return null;
  }

  #animate(from, to) {
    const node = this.#textNode();
    if (!node) return;

    const finalText = node.nodeValue;
    const match = finalText.match(/\d[\d,.]*\d|\d/);
    if (!match) return;

    const decimalMatch = match[0].match(/\.(\d+)$/);
    const decimals = decimalMatch ? decimalMatch[1].length : 0;
    const options = { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
    // Pick the grouping style (Indian 1,23,456 or Western 123,456) that reproduces the server text.
    const formatter = ['en-IN', 'en-US']
      .map((locale) => new Intl.NumberFormat(locale, options))
      .find((candidate) => candidate.format(to) === match[0]);
    if (!formatter) return;

    const before = finalText.slice(0, match.index);
    const after = finalText.slice(match.index + match[0].length);
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      node.nodeValue = progress < 1 ? before + formatter.format(from + (to - from) * eased) + after : finalText;
      if (progress < 1) this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('s-count-up')) customElements.define('s-count-up', SCountUp);
